import React, {useState} from 'react';
import {Alert, Badge, Button, Spinner} from 'react-bootstrap';
import KeyIcon from '@mui/icons-material/VpnKeyRounded';
import {tenantsApi, getTenantErrorMessage} from '../../../tenants/services/tenantsApi';
import CopyableCommand from '../../../../shared/ui/CopyableCommand';
import {useEdges} from '../../../edges/context/EdgesContext';
import {useEventGridProvisioningEnabled} from '../../../../shared/constants/featureFlags';

/**
 * Reveals / regenerates the Event Grid access key (SCCCLOUD_KEY) for an edge and
 * shows both the raw key (to paste into the Balena SCCCLOUD_KEY env var for
 * edges being migrated) and the docker run command (for 3rd-party/EG-only
 * hardware). Minting a fresh key does not invalidate the previous one;
 * revocation is handled server-side in the Event Grid namespace.
 *
 * Rendered for any edge once the Remote Config flag is on: legacy Balena
 * edges get a "Generate Key" action to start migrating to Event Grid config
 * delivery, while keeping balenaUuid for device management.
 */
const EdgeAccessKey = ({eid}) => {
  const {edges, updateEdgeState} = useEdges();
  const eventGridProvisioningEnabled = useEventGridProvisioningEnabled();
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [accessKey, setAccessKey] = useState(null);
  const [dockerCommand, setDockerCommand] = useState(null);
  const [error, setError] = useState(null);

  const edge = edges.find((e) => e.id === eid);

  if (!eventGridProvisioningEnabled || !edge) {
    return null;
  }

  const hasKey = !!edge.sccCloudKey;
  const isBalenaManaged = !!edge.balenaUuid;
  const isMigrated = edge.configSource === 'eventgrid';
  const migrationState = isBalenaManaged ? (hasKey ? (isMigrated ? 'Migrated' : 'Migrating') : 'Legacy') : null;
  const migrationBadgeVariant = {Legacy: 'secondary', Migrating: 'warning', Migrated: 'success'}[migrationState];

  const handleRegenerate = async () => {
    setLoading(true);
    setError(null);
    setAccessKey(null);
    setDockerCommand(null);

    const result = await tenantsApi.regenerateSccCloudKey(eid);

    setLoading(false);

    if (!result.ok) {
      setError(getTenantErrorMessage(result.error, 'Failed to generate access key.'));
      return;
    }

    setAccessKey(result.data?.accessKey || null);
    setDockerCommand(result.data?.dockerCommand || null);
    updateEdgeState(eid, {sccCloudKey: true});
  };

  const handleCompleteMigration = async () => {
    setMigrating(true);
    setError(null);

    const result = await tenantsApi.completeEdgeMigration(eid);

    setMigrating(false);

    if (!result.ok) {
      setError(getTenantErrorMessage(result.error, 'Failed to complete edge migration.'));
      return;
    }

    updateEdgeState(eid, {configSource: 'eventgrid'});
  };

  return (
    <div>
      <h5 className="mb-3" style={{color: 'var(--text-primary)'}}>
        <KeyIcon className="me-2" style={{marginBottom: 3, fontSize: 20}}/>Access Key
        {migrationState && <Badge bg={migrationBadgeVariant} className="ms-2 align-middle">{migrationState}</Badge>}
      </h5>

      <p className="mb-2" style={{fontSize: 14, color: 'var(--text-secondary)'}}>
        Generate a fresh <code>SCCCLOUD_KEY</code>. The previous key stays valid
        until it expires or the client is revoked in the broker.
        {isBalenaManaged && ' For a Balena-managed SCC, paste the raw key into the SCCCLOUD_KEY environment variable in the Balena portal.'}
      </p>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {accessKey && (
        <div className="mb-3">
          <CopyableCommand value={accessKey} label="SCCCLOUD_KEY"/>
        </div>
      )}

      {dockerCommand && (
        <div className="mb-3">
          <CopyableCommand value={dockerCommand} label="docker run command"/>
        </div>
      )}

      <Button variant="outline-primary" onClick={handleRegenerate} disabled={loading} className="me-2">
        {loading ? <><Spinner size="sm" className="me-1"/>Generating...</> : (hasKey ? 'Reveal / Regenerate Key' : 'Generate Key')}
      </Button>

      {migrationState === 'Migrating' && (
        <Button variant="outline-success" onClick={handleCompleteMigration} disabled={migrating}>
          {migrating ? <><Spinner size="sm" className="me-1"/>Completing...</> : 'Complete migration to Event Grid'}
        </Button>
      )}
    </div>
  );
};

export default EdgeAccessKey;

